//import { ensureActiveSession } from "@lib/auth/ensureActiveSession";
import { COMMERCETOOLS_API_URL } from "@lib/constants/urlConstants";
import { useStoreApi } from "@store/index";
import { createClient, mapExchange } from "@urql/core";
import { authExchange } from "@urql/exchange-auth";
import type { AuthConfig } from "@urql/exchange-auth";
import type { Session } from "next-auth";
import { getSession, signOut, useSession } from "next-auth/react";
import { useMemo } from "react";
import { dedupExchange, fetchExchange, makeOperation } from "urql";

const addAuthToOperation: AuthConfig<Session>["addAuthToOperation"] = ({
    authState,
    operation,
}) => {
    // the token isn't in the auth state, return the operation without changes
    if (!authState?.accessToken) {
        return operation;
    }

    // fetchOptions can be a function (See Client API) but you can simplify this based on usage
    const fetchOptions =
        typeof operation.context.fetchOptions === "function"
            ? operation.context.fetchOptions()
            : operation.context.fetchOptions || {};

    const headers = {
        ...fetchOptions.headers,
    };

    headers["Authorization"] = `Bearer ${authState.accessToken}`;

    return makeOperation(operation.kind, operation, {
        ...operation.context,
        fetchOptions: {
            ...fetchOptions,
            headers,
        },
    });
};

// By always returning true, we will force every call
// to call getAuth and update the session token in the header.
const willAuthError = () => true;

/**
 * Get GraphQL Client in browser environments (frontend).
 *
 * If the user has an active session, it will add an accessToken to all requests
 */
const useClient = (options?: RequestInit) => {
    const api = useStoreApi();
    const { data: session, status } = useSession();

    return useMemo(() => {
        const getAuth: AuthConfig<Session>["getAuth"] = async () => {
            if (status !== "authenticated") {
                return null;
            }
            // We currently have an issue with the GraphQL client not receiving updated tokens in time when calls are made within a single function.
            // This is a workaround to ensure we get an updated token.
            return await getSession();

            //return await ensureActiveSession(session);
        };
        return createClient({
            url: `${COMMERCETOOLS_API_URL}/${process.env.NEXT_PUBLIC_COMMERCETOOLS_PROJECT_KEY}/graphql`,
            exchanges: [
                dedupExchange,
                mapExchange({
                    async onError(error) {
                        const isInvalidTokenError = error.graphQLErrors.some(
                            (e) => e.message === "invalid_token"
                        );

                        if (isInvalidTokenError) {
                            api.getState().clearMe();
                            await signOut({ redirect: false });
                        }
                    },
                }),

                authExchange<Session>({
                    getAuth,
                    addAuthToOperation,
                    willAuthError,
                }),
                fetchExchange,
            ],
        });
    }, [options, session, status, api]);
};

export default useClient;
